package er.prateek.crossly.generation

import er.prateek.crossly.data.model.Clue
import er.prateek.crossly.data.model.ClueDirection
import er.prateek.crossly.data.model.DifficultyTier
import er.prateek.crossly.data.model.Puzzle
import er.prateek.crossly.data.model.WordBank
import er.prateek.crossly.data.model.WordEntry
import er.prateek.crossly.game.crossword.PuzzleRandom

private const val EMPTY = ' '

/** Try-order sentinel for a candidate excluded from this puzzle entirely. */
private const val EXCLUDED = Int.MAX_VALUE

/**
 * Candidates of one length, indexed by (position, letter) so a partially filled
 * slot can be narrowed without scanning the whole pool.
 *
 * The dense grids this game uses (3x3 and 4x4 fully open, 5x5 cut-corner) are
 * heavily over-constrained -- every cell is a crossing -- so a naive filter runs
 * the full pool at every node and the search crawls. Narrowing by the most
 * selective known letter first turns the inner loop from thousands of string
 * comparisons into tens.
 */
private class LengthIndex(val entries: List<WordEntry>) {
    // byPosition[i]['A'..'Z'] -> indices into entries whose i-th letter is that.
    private val byPosition: List<Array<MutableList<Int>>> =
        if (entries.isEmpty()) emptyList() else List(entries[0].word.length) {
            Array(26) { mutableListOf<Int>() }
        }

    init {
        for ((index, entry) in entries.withIndex()) {
            for ((position, char) in entry.word.withIndex()) {
                byPosition[position][char - 'A'].add(index)
            }
        }
    }

    /** Entry indices matching [pattern], where [EMPTY] is a wildcard. */
    fun matching(pattern: CharArray): List<Int> {
        var best: List<Int>? = null
        for ((position, char) in pattern.withIndex()) {
            if (char == EMPTY) continue
            val bucket = byPosition[position][char - 'A']
            if (best == null || bucket.size < best.size) best = bucket
        }
        val candidates = best ?: entries.indices.toList()
        return candidates.filter { index ->
            val word = entries[index].word
            pattern.withIndex().all { (position, char) -> char == EMPTY || word[position] == char }
        }
    }
}

/** A filled grid: the word chosen for every slot, in template slot order. */
private class Fill(val words: List<String>)

/**
 * Builds complete crosswords from the bundled [WordBank] by seeded backtracking
 * search, so the app can ship a handful of grid shapes instead of thousands of
 * hand-authored puzzles.
 *
 * Two guarantees callers depend on:
 * - **No word repeats inside a puzzle.** This is enforced in the search itself
 *   rather than checked afterwards, so a grid that could only be filled by
 *   repeating a word is rejected and re-searched instead of shipped.
 * - **The same seed always yields the same puzzle**, on every platform and
 *   build. Progress is stored per puzzle id, so a level whose content drifted
 *   would silently orphan the player's stars.
 */
class PuzzleGenerator(
    private val bank: WordBank,
    /**
     * Smallest candidate pool a slot may be filled from; shallower buckets
     * widen into harder tiers. Drives cross-level variety far more strongly
     * than the search heuristics do -- see [WordBank.fillPoolFor].
     */
    private val minPoolSize: Int = 1600,
) {

    private val indexCache = mutableMapOf<Pair<Int, DifficultyTier>, LengthIndex>()

    private fun index(length: Int, tier: DifficultyTier): LengthIndex =
        indexCache.getOrPut(length to tier) {
            LengthIndex(bank.fillPoolFor(length, tier, minPoolSize))
        }

    /**
     * Fills [template] with words at or below [tier]. Returns null if no
     * consistent fill was found within [nodeBudget] search steps -- callers
     * should try another seed rather than treating it as fatal.
     *
     * [avoidWords] keeps recently-used answers out of the next few levels. With
     * [avoidStrictly] they are removed from the pool outright, which is what
     * actually produces variety -- merely *preferring* unseen words is too weak
     * on a dense grid, because the search converges on the handful of words
     * that cross well and reuses them anyway. Strict mode can make a grid
     * unfillable, so callers should fall back to a non-strict attempt.
     */
    fun generate(
        template: GridTemplate,
        tier: DifficultyTier,
        seed: Long,
        id: String,
        title: String,
        avoidWords: Set<String> = emptySet(),
        avoidStrictly: Boolean = false,
        nodeBudget: Int = 60_000,
    ): Puzzle? {
        val random = PuzzleRandom(seed)
        val slots = template.slots
        val grid = CharArray(template.width * template.height) { EMPTY }
        val chosen = arrayOfNulls<String>(slots.size)
        val used = mutableSetOf<String>()
        var budget = nodeBudget

        // Precomputed candidate order per slot: seeded once so the search is
        // deterministic, and biased to try non-avoided words first.
        val orderBySlot = slots.map { slot ->
            val pool = index(slot.length, tier)
            val shuffled = random.shuffled(pool.entries.indices.toList())
            if (avoidStrictly) {
                shuffled.filter { pool.entries[it].word !in avoidWords }
            } else {
                shuffled.sortedBy { if (pool.entries[it].word in avoidWords) 1 else 0 }
            }
        }
        // Position of each entry in its slot's try-order. Entries missing from
        // the order (filtered out by strict avoidance) keep the sentinel and are
        // skipped by the search -- sizing this by the *pool*, not the order, and
        // defaulting to the sentinel is what stops an excluded word from
        // sorting to the front with rank 0.
        val rankInOrder = slots.mapIndexed { slotIndex, slot ->
            val poolSize = index(slot.length, tier).entries.size
            IntArray(poolSize) { EXCLUDED }.also { positions ->
                orderBySlot[slotIndex].forEachIndexed { position, entryIndex ->
                    positions[entryIndex] = position
                }
            }
        }

        fun patternFor(slot: Slot): CharArray =
            CharArray(slot.length) { grid[slot.cellIndex(it, template.width)] }

        fun place(slot: Slot, word: String) {
            for (i in 0 until slot.length) grid[slot.cellIndex(i, template.width)] = word[i]
        }

        fun search(filled: Int): Boolean {
            if (filled == slots.size) return true
            if (budget-- <= 0) return false

            // Most-constrained slot first: fail fast on the tightest crossing.
            var bestSlot = -1
            var bestCandidates: List<Int>? = null
            for ((slotIndex, slot) in slots.withIndex()) {
                if (chosen[slotIndex] != null) continue
                val candidates = index(slot.length, tier).matching(patternFor(slot))
                if (bestCandidates == null || candidates.size < bestCandidates.size) {
                    bestSlot = slotIndex
                    bestCandidates = candidates
                    if (candidates.isEmpty()) break
                }
            }
            val slotIndex = bestSlot
            val candidates = bestCandidates ?: return false
            if (candidates.isEmpty()) return false

            val slot = slots[slotIndex]
            val pool = index(slot.length, tier)
            val snapshot = patternFor(slot)

            val ranks = rankInOrder[slotIndex]
            for (entryIndex in candidates.filter { ranks[it] != EXCLUDED }.sortedBy { ranks[it] }) {
                val word = pool.entries[entryIndex].word
                if (word in used) continue

                place(slot, word)
                chosen[slotIndex] = word
                used.add(word)

                if (search(filled + 1)) return true

                used.remove(word)
                chosen[slotIndex] = null
                for (i in 0 until slot.length) {
                    grid[slot.cellIndex(i, template.width)] = snapshot[i]
                }
            }
            return false
        }

        if (!search(0)) return null
        return assemble(template, tier, seed, id, title, Fill(chosen.map { it!! }))
    }

    private fun assemble(
        template: GridTemplate,
        tier: DifficultyTier,
        seed: Long,
        id: String,
        title: String,
        fill: Fill,
    ): Puzzle {
        // A separate stream from the fill search, so picking clue variants can
        // never perturb which words were chosen.
        val clueRandom = PuzzleRandom(seed * 31 + 7)
        val entryByWord = mutableMapOf<String, WordEntry>()

        val across = mutableListOf<Clue>()
        val down = mutableListOf<Clue>()
        for ((slotIndex, slot) in template.slots.withIndex()) {
            val word = fill.words[slotIndex]
            val entry = entryByWord.getOrPut(word) {
                bank.fillPoolFor(slot.length, tier, minPoolSize).first { it.word == word }
            }
            val clueText = entry.clues[clueRandom.nextInt(entry.clues.size)]
            val clue = Clue(
                number = template.numberByCell.getValue(slot.row * template.width + slot.col),
                row = slot.row,
                col = slot.col,
                answer = word,
                clue = clueText,
            )
            if (slot.direction == ClueDirection.ACROSS) across.add(clue) else down.add(clue)
        }

        return Puzzle(
            id = id,
            packId = GENERATED_PACK_ID,
            title = title,
            difficulty = tier,
            width = template.width,
            height = template.height,
            grid = template.mask,
            cluesAcross = across,
            cluesDown = down,
        )
    }

    companion object {
        const val GENERATED_PACK_ID = "generated-crossword-v1"
    }
}
